import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, Trash2, Edit3, ShieldCheck, KeyRound, Users as UsersIcon, Building2, Truck, Briefcase, Shield } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";

const SCOPE_ICONS = { interno: Briefcase, cliente: Building2, unidad: Truck };
const SCOPE_LABELS = { interno: "Equipo interno", cliente: "Portal cliente", unidad: "Acceso de ambulancia" };

const EMPTY_FORM = { email: "", name: "", password: "", role: "cabina", phone: "", client_id: "", unit_id: "" };

export default function Users() {
    const [list, setList] = useState([]);
    const [roles, setRoles] = useState([]);
    const [clients, setClients] = useState([]);
    const [units, setUnits] = useState([]);
    const [filterScope, setFilterScope] = useState("all");
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [resetPwd, setResetPwd] = useState("");
    const [form, setForm] = useState(EMPTY_FORM);

    const load = async () => {
        try {
            const [u, r, c, un] = await Promise.all([
                api.get("/admin/users"),
                api.get("/admin/roles-catalog"),
                api.get("/clients"),
                api.get("/units"),
            ]);
            setList(u.data || []);
            setRoles(r.data || []);
            setClients(c.data || []);
            setUnits(un.data || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error cargando usuarios");
        }
    };
    useEffect(() => { load(); }, []);

    const roleByVal = useMemo(() => Object.fromEntries(roles.map(r => [r.value, r])), [roles]);

    const filteredList = useMemo(() => {
        if (filterScope === "all") return list;
        return list.filter(u => (roleByVal[u.role]?.scope) === filterScope);
    }, [list, filterScope, roleByVal]);

    const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setOpen(true); };
    const openEdit = (u) => {
        setEditingId(u.id);
        setForm({ email: u.email, name: u.name, password: "", role: u.role, phone: u.phone || "", client_id: u.client_id || "", unit_id: u.unit_id || "" });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        const r = roleByVal[form.role];
        if (r?.scope === "cliente" && !form.client_id) return toast.error("Selecciona un cliente");
        if (r?.scope === "unidad" && !form.unit_id) return toast.error("Selecciona una unidad");
        if (!editingId && (form.password || "").length < 6) return toast.error("Contraseña mínima 6 caracteres");

        try {
            if (editingId) {
                const payload = { name: form.name, role: form.role, phone: form.phone, client_id: form.client_id || null, unit_id: form.unit_id || null };
                if (form.password) payload.password = form.password;
                await api.put(`/admin/users/${editingId}`, payload);
                toast.success("Usuario actualizado");
            } else {
                await api.post("/admin/users", form);
                toast.success("Usuario creado");
            }
            setOpen(false); setEditingId(null); setForm(EMPTY_FORM);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const toggleActive = async (u) => {
        try {
            await api.put(`/admin/users/${u.id}`, { active: !u.active });
            toast.success(u.active ? "Usuario deshabilitado" : "Usuario habilitado");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const remove = async (u) => {
        if (!window.confirm(`¿Eliminar acceso de "${u.name}" (${u.email})?\n\nEsta acción se registra en Auditoría.`)) return;
        try { await api.delete(`/admin/users/${u.id}`); toast.success("Eliminado"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const submitReset = async () => {
        if (resetPwd.length < 6) return toast.error("Mínimo 6 caracteres");
        try {
            await api.put(`/admin/users/${resetting.id}`, { password: resetPwd });
            toast.success(`Contraseña reseteada para ${resetting.email}`);
            setResetting(null); setResetPwd("");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const counts = useMemo(() => ({
        interno: list.filter(u => roleByVal[u.role]?.scope === "interno").length,
        cliente: list.filter(u => roleByVal[u.role]?.scope === "cliente").length,
        unidad: list.filter(u => roleByVal[u.role]?.scope === "unidad").length,
    }), [list, roleByVal]);

    const currentRole = roleByVal[form.role];

    return (
        <div className="space-y-4" data-testid="users-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <Shield className="w-6 h-6 text-red-600" /> Accesos del sistema
                        <HelpTip text="Aquí administras TODAS las cuentas que pueden entrar a la plataforma: tu equipo interno, los clientes corporativos del portal, y los accesos de cada ambulancia (para tablet a bordo)." />
                    </h2>
                    <div className="text-xs text-slate-500 mt-1 font-mono">
                        {list.length} total · {counts.interno} internos · {counts.cliente} clientes · {counts.unidad} unidades
                    </div>
                </div>
                <button onClick={openNew} className="fm-btn-accent" data-testid="new-user-btn">
                    <Plus className="w-3.5 h-3.5" /> Nuevo acceso
                </button>
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap gap-2">
                {[
                    { v: "all", l: `Todos (${list.length})`, icon: UsersIcon },
                    { v: "interno", l: `Equipo interno (${counts.interno})`, icon: Briefcase },
                    { v: "unidad", l: `Ambulancias (${counts.unidad})`, icon: Truck },
                    { v: "cliente", l: `Clientes portal (${counts.cliente})`, icon: Building2 },
                ].map((f) => {
                    const Icon = f.icon;
                    const active = filterScope === f.v;
                    return (
                        <button
                            key={f.v}
                            onClick={() => setFilterScope(f.v)}
                            className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition ${active ? "bg-red-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}
                            data-testid={`filter-${f.v}`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {f.l}
                        </button>
                    );
                })}
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["Nombre", "Email", "Rol", "Asociado a", "Estado", "Acciones"].map((h) => (
                            <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {filteredList.map((u) => {
                            const r = roleByVal[u.role];
                            const ScopeIcon = SCOPE_ICONS[r?.scope] || UsersIcon;
                            return (
                                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3 font-medium">{u.name}</td>
                                    <td className="py-2 px-3 font-mono text-xs">{u.email}</td>
                                    <td className="py-2 px-3">
                                        <span className="fm-badge fm-badge-neutral inline-flex items-center gap-1">
                                            <ScopeIcon className="w-3 h-3" /> {r?.label || u.role}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 text-xs">{u.client_name || u.unit_label || <span className="text-slate-400">—</span>}</td>
                                    <td className="py-2 px-3">
                                        <button onClick={() => toggleActive(u)} className={`fm-badge cursor-pointer hover:opacity-80 ${u.active ? "fm-badge-positive" : "fm-badge-neutral"}`} title="Click para habilitar/deshabilitar" data-testid={`toggle-active-${u.id}`}>
                                            {u.active ? "Activo" : "Deshabilitado"}
                                        </button>
                                    </td>
                                    <td className="py-2 px-3">
                                        <div className="flex items-center gap-3 text-xs">
                                            <button onClick={() => openEdit(u)} className="text-slate-600 hover:text-red-600 inline-flex items-center gap-1" data-testid={`edit-user-${u.id}`}><Edit3 className="w-3.5 h-3.5" /> Editar</button>
                                            <button onClick={() => { setResetting(u); setResetPwd(""); }} className="text-amber-700 hover:text-amber-900 inline-flex items-center gap-1" data-testid={`reset-pwd-${u.id}`}><KeyRound className="w-3.5 h-3.5" /> Contraseña</button>
                                            <button onClick={() => remove(u)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filteredList.length === 0 && (
                    <div className="p-10 text-center text-sm text-slate-500"><ShieldCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />Sin usuarios para este filtro.</div>
                )}
            </div>

            {/* Modal Crear/Editar */}
            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-xl w-full max-h-[92vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="font-display font-bold text-lg">{editingId ? "Editar acceso" : "Nuevo acceso"}</h2>
                            <button onClick={() => { setOpen(false); setEditingId(null); }}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div>
                                <label className="fm-label">Rol *</label>
                                <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="fm-input" data-testid="user-role-select">
                                    <optgroup label="Equipo interno">
                                        {roles.filter(r => r.scope === "interno").map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </optgroup>
                                    <optgroup label="Acceso operativo">
                                        {roles.filter(r => r.scope === "unidad").map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </optgroup>
                                    <optgroup label="Cliente corporativo">
                                        {roles.filter(r => r.scope === "cliente").map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </optgroup>
                                </select>
                                {currentRole && <div className="text-[11px] text-slate-500 mt-1 italic">{currentRole.description}</div>}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2"><label className="fm-label">Nombre *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fm-input" data-testid="user-name" /></div>
                                <div><label className="fm-label">Email *</label><input required type="email" disabled={!!editingId} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="fm-input" data-testid="user-email" /></div>
                                <div><label className="fm-label">Teléfono</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="fm-input" placeholder="+52..." /></div>
                                {!editingId && (
                                    <div className="col-span-2"><label className="fm-label">Contraseña inicial *</label><input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="fm-input font-mono" placeholder="Mínimo 6 caracteres" data-testid="user-password" /></div>
                                )}
                            </div>

                            {currentRole?.scope === "cliente" && (
                                <div>
                                    <label className="fm-label">Cliente corporativo *</label>
                                    <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="fm-input" data-testid="user-client">
                                        <option value="">Seleccionar...</option>
                                        {clients.map(c => <option key={c.id} value={c.id}>{c.business_name}</option>)}
                                    </select>
                                </div>
                            )}

                            {currentRole?.scope === "unidad" && (
                                <div>
                                    <label className="fm-label">Unidad ambulancia *</label>
                                    <select required value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} className="fm-input" data-testid="user-unit">
                                        <option value="">Seleccionar...</option>
                                        {units.map(u => <option key={u.id} value={u.id}>{u.economic_number} · {u.plates}</option>)}
                                    </select>
                                    <div className="text-[11px] text-slate-500 mt-1 bg-amber-50 px-2 py-1 rounded-sm border-l-2 border-l-amber-400">
                                        💡 Este acceso va en la tablet/celular dentro de la ambulancia. La unidad envía GPS automáticamente y la tripulación llena el FRAP.
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="fm-btn-outline">Cancelar</button>
                                <button type="submit" className="fm-btn-accent" data-testid="save-user-btn">{editingId ? "Actualizar" : "Crear acceso"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Reset Password */}
            {resetting && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-sm w-full">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-600" /> Reset de contraseña</h3>
                            <button onClick={() => setResetting(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-sm">
                                <span className="text-slate-500">Usuario:</span> <span className="font-mono">{resetting.email}</span>
                            </div>
                            <div>
                                <label className="fm-label">Nueva contraseña *</label>
                                <input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="fm-input font-mono" placeholder="Mínimo 6 caracteres" autoFocus data-testid="reset-pwd-input" />
                                <div className="text-[10px] text-slate-500 mt-1">Comparte esta contraseña con el usuario en un canal seguro. El sistema NO la envía por correo.</div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={submitReset} className="fm-btn-primary flex-1 justify-center" data-testid="confirm-reset-pwd">Actualizar contraseña</button>
                                <button onClick={() => setResetting(null)} className="fm-btn-outline">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
